import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Minus, Plus, X } from "lucide-react";
import {
  PANTRY_LOCATIONS,
  de,
  formatQuantity,
  type LowStock,
  type PantryItem,
  type PantryLocation,
} from "@ainkauf/core";
import {
  addPantryItem,
  adjustPantryItem,
  allPantryItems,
  deletePantryItem,
  lowStockItems,
  updatePantryItem,
} from "@/db/repository";

/**
 * Der Vorrat: was da ist, und ab wann es nachgekauft gehört.
 *
 * Gruppiert nach Ort, weil man beim Nachschauen durch die Wohnung geht und
 * nicht durch das Alphabet. Plus und Minus sind große Knöpfe: Bestände
 * ändert man im Vorbeigehen, mit einer Hand, oft mit der Kühlschranktür in
 * der anderen.
 */
export function PantryList({ onRestock }: { onRestock: () => void }) {
  const [neu, setNeu] = useState("");
  const [offen, setOffen] = useState<string | null>(null);

  const vorraete = useLiveQuery(() => allPantryItems(), [], [] as PantryItem[]);
  const knapp = useLiveQuery(() => lowStockItems(), [], [] as LowStock[]);

  /** Erst leeren, dann schreiben, sonst geht die nächste Eingabe verloren. */
  async function anlegen() {
    const eingabe = neu.trim();
    if (eingabe.length === 0) return;
    setNeu("");
    await addPantryItem(eingabe);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <input
          value={neu}
          onChange={(event) => {
            setNeu(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") void anlegen();
          }}
          placeholder={de.pantry.namePlaceholder}
          aria-label={de.pantry.nameLabel}
          data-testid="pantry-input"
          className="min-h-[52px] min-w-0 flex-1 rounded-[var(--radius-lg)] bg-card px-4 text-base text-ink shadow-[var(--shadow-card)] outline-none focus:ring-2 focus:ring-accent"
        />
        <button
          type="button"
          onClick={() => void anlegen()}
          aria-label={de.pantry.add}
          data-testid="pantry-add"
          className="inline-flex size-[52px] shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-accent text-white shadow-[var(--shadow-card)] transition-transform active:scale-95"
        >
          <Plus size={22} strokeWidth={2.25} aria-hidden />
        </button>
      </div>

      {knapp.length > 0 && (
        <section
          className="flex flex-col gap-2 rounded-[var(--radius-xl)] px-4 py-4"
          style={{ background: "var(--honey-soft)" }}
          data-testid="pantry-low"
        >
          <span className="text-[13px] font-bold uppercase tracking-[0.1em] text-ink-2">{de.pantry.lowTitle}</span>
          <span className="num text-lg font-semibold text-ink">{de.pantry.lowCount(knapp.length)}</span>
          <ul className="flex flex-wrap gap-1.5">
            {knapp.map((eintrag) => (
              <li key={eintrag.item.id} className="rounded-full bg-card px-3 py-1 text-sm">
                {eintrag.item.name}
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onRestock}
            data-testid="pantry-low-to-list"
            className="mt-1 min-h-[44px] rounded-full bg-accent px-5 font-semibold text-white transition-transform active:scale-[0.99]"
          >
            {de.pantry.lowToList}
          </button>
        </section>
      )}

      {vorraete.length === 0 ? (
        <p className="karte px-5 py-10 text-center text-ink-2" data-testid="pantry-empty">
          {de.pantry.empty}
          <span className="mt-1 block text-sm">{de.pantry.emptyHint}</span>
        </p>
      ) : (
        PANTRY_LOCATIONS.filter((ort) => vorraete.some((zeile) => zeile.location === ort)).map((ort) => (
          <section key={ort} className="flex flex-col gap-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.08em] text-ink-2">{de.pantry.locations[ort]}</h3>
            <ul className="flex flex-col gap-2">
              {vorraete
                .filter((zeile) => zeile.location === ort)
                .map((zeile) => (
                  <li key={zeile.id} className="karte flex flex-col" data-testid="pantry-row" data-name={zeile.name}>
                    <div className="flex items-center gap-2 px-3">
                      <button
                        type="button"
                        onClick={() => {
                          setOffen(offen === zeile.id ? null : zeile.id);
                        }}
                        aria-expanded={offen === zeile.id}
                        data-testid="pantry-open"
                        data-name={zeile.name}
                        className="flex min-h-[60px] min-w-0 flex-1 flex-col justify-center pl-1 text-left"
                      >
                        <span className="truncate font-medium">{zeile.name}</span>
                        {zeile.min_quantity !== null && (
                          <span className="num truncate text-sm text-ink-2">
                            {de.pantry.minLabel}: {formatQuantity({ quantity: zeile.min_quantity, unit: zeile.unit })}
                          </span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => void adjustPantryItem(zeile.id, -1)}
                        aria-label={`${de.pantry.minus}: ${zeile.name}`}
                        data-testid="pantry-minus"
                        data-name={zeile.name}
                        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-paper-2 text-ink-2 transition-transform active:scale-90"
                      >
                        <Minus size={18} strokeWidth={2} aria-hidden />
                      </button>

                      <span
                        className="num w-20 shrink-0 text-center text-base font-bold"
                        data-testid="pantry-quantity"
                        data-name={zeile.name}
                      >
                        {formatQuantity({ quantity: zeile.quantity, unit: zeile.unit })}
                      </span>

                      <button
                        type="button"
                        onClick={() => void adjustPantryItem(zeile.id, 1)}
                        aria-label={`${de.pantry.plus}: ${zeile.name}`}
                        data-testid="pantry-plus"
                        data-name={zeile.name}
                        className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-paper-2 text-ink-2 transition-transform active:scale-90"
                      >
                        <Plus size={18} strokeWidth={2} aria-hidden />
                      </button>
                    </div>

                    {offen === zeile.id && <PantryDetail zeile={zeile} onClose={() => { setOffen(null); }} />}
                  </li>
                ))}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

/** Mindestbestand und Ort. Selten gebraucht, deshalb erst beim Antippen. */
function PantryDetail({ zeile, onClose }: { zeile: PantryItem; onClose: () => void }) {
  const [minimum, setMinimum] = useState(zeile.min_quantity === null ? "" : String(zeile.min_quantity));

  return (
    <div className="flex flex-col gap-3 border-t border-paper-3 px-4 py-4">
      <label className="flex flex-col gap-1.5 text-sm text-ink-2">
        {de.pantry.minLabel}
        <input
          value={minimum}
          onChange={(event) => {
            setMinimum(event.target.value);
            const zahl = Number.parseFloat(event.target.value.replace(",", "."));
            void updatePantryItem(zeile.id, { min_quantity: Number.isFinite(zahl) && zahl >= 0 ? zahl : null });
          }}
          inputMode="decimal"
          data-testid="pantry-min"
          data-name={zeile.name}
          className="min-h-[52px] rounded-[var(--radius-lg)] bg-paper-2 px-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-xs">{de.pantry.minHint}</span>
      </label>

      <label className="flex flex-col gap-1.5 text-sm text-ink-2">
        {de.pantry.locationLabel}
        <select
          value={zeile.location}
          onChange={(event) => void updatePantryItem(zeile.id, { location: event.target.value as PantryLocation })}
          data-testid="pantry-location"
          data-name={zeile.name}
          className="min-h-[52px] rounded-[var(--radius-lg)] bg-paper-2 px-4 text-base text-ink outline-none focus:ring-2 focus:ring-accent"
        >
          {PANTRY_LOCATIONS.map((ort) => (
            <option key={ort} value={ort}>
              {de.pantry.locations[ort]}
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        onClick={() => {
          onClose();
          void deletePantryItem(zeile.id);
        }}
        data-testid="pantry-delete"
        data-name={zeile.name}
        className="inline-flex min-h-[44px] items-center justify-center gap-2 self-start rounded-full px-4 text-sm font-semibold text-accent"
      >
        <X size={16} strokeWidth={2} aria-hidden />
        {de.common.delete}
      </button>
    </div>
  );
}
